import { type ReactNode, useCallback } from "react";

import type { CommandContextEnum } from "../../core/domain/command-context.enum";
import type { CommandIdentifierEnum } from "../../core/domain/command-identifier.enum";
import { useCommandShortcutContext } from "../CommandShortcutProvider";
import { type CommandIdentifierHandler, useCommandPaletteContext } from "./context";

export type CommandPaletteMethod = {
  register: (...commandIdentifierHandlers: CommandIdentifierHandler[]) => void;
  unregister: (...identifiers: CommandIdentifierEnum[]) => void;
  switchContext: (context: CommandContextEnum, header?: ReactNode) => void;
};

const useCommandPalette = (): CommandPaletteMethod => {
  const { set: setListener, remove: removeListener, get: getListener } = useCommandShortcutContext();
  const { commandHandler, enabledCommandShortcut, switchContext } = useCommandPaletteContext();

  const register = useCallback(
    (...commandIdentifierHandlers: CommandIdentifierHandler[]) => {
      commandIdentifierHandlers.forEach(handler => {
        // store commandHandler for that identifier
        commandHandler.current.set(handler.identifier, handler);

        // check if commandShortcut is enabled from backend
        const enabledCommand = enabledCommandShortcut.current.get(handler.identifier);
        if (!enabledCommand) return;

        // check if there is commandShortcut listener existed
        const existedListener = getListener(enabledCommand.shortcut);
        if (existedListener) return;

        // create commandShortcut listener if it's existed yet
        setListener({
          name: handler.identifier,
          commandFn: handler.commandFn,
          shortcut: enabledCommand.shortcut,
        });
      });
    },
    [commandHandler, enabledCommandShortcut, getListener, setListener]
  );

  const unregister = useCallback(
    (...identifiers: CommandIdentifierEnum[]) => {
      identifiers.forEach(identifier => {
        // remove commandHandler for that identifier
        commandHandler.current.delete(identifier);

        // check if commandShortcut is enabled from backend
        const enabledCommand = enabledCommandShortcut.current.get(identifier);
        if (!enabledCommand) return;

        // check if there is commandShortcut listener existed
        const existedListener = getListener(enabledCommand.shortcut);
        if (!existedListener) return;

        // completely remove related things
        removeListener(enabledCommand.shortcut);

        // do not remove enabledCommand for re-register
        // enabledCommandShortcut.current.delete(enabledCommand.identifier);
      });
    },
    [commandHandler, enabledCommandShortcut, getListener, removeListener]
  );

  return { register, unregister, switchContext };
};

export default useCommandPalette;
